'use client';

import * as AvatarPrimitive from '@radix-ui/react-avatar';
import * as React from 'react';

import { avatarColor } from '../lib/avatar-color';
import { cn } from '../lib/utils';

export type AvatarProps = {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
} & React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>;

const Avatar = React.forwardRef<React.ComponentRef<typeof AvatarPrimitive.Root>, AvatarProps>(
  ({ className, size, ...props }, ref) => (
    <AvatarPrimitive.Root
      ref={ref}
      className={cn(
        'relative flex h-8 w-8 shrink-0 overflow-hidden rounded-full',
        {
          'h-5 w-5 text-xs': size === 'xs',
          'h-6 w-6 text-xs': size === 'sm',
          'h-9 w-9 text-sm': size === 'lg',
          'h-12 w-12 text-lg': size === 'xl',
        },
        className,
      )}
      {...props}
    />
  ),
);
Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = React.forwardRef<
  React.ComponentRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image ref={ref} className={cn('aspect-square h-full w-full', className)} {...props} />
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

const AvatarFallback = React.forwardRef<
  React.ComponentRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback> & {
    variant?: 'default' | 'primary';
    /* A user ID. It replaces the variant colour with a colour that stays the same for that user. */
    colorSeed?: string;
  }
>(({ className, variant, colorSeed, style, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      'flex h-full w-full items-center justify-center rounded-full bg-accent leading-none text-accent-foreground',
      {
        'bg-primary text-primary-foreground': variant === 'primary' && !colorSeed,
        'text-darkest': colorSeed,
      },
      className,
    )}
    // Inline, because Tailwind cannot generate a class for a hue it does not see at build time.
    style={colorSeed ? { backgroundColor: avatarColor(colorSeed), ...style } : style}
    {...props}
  />
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

export { Avatar, AvatarImage, AvatarFallback };
